// src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Marketplace from './pages/Marketplace';
import ListingDetail from './pages/ListingDetail';
import Trade from './pages/Trade';
import Portfolio from './pages/Portfolio';
import Transactions from './pages/Transactions';
import Profile from './pages/Profile';
import KYC from './pages/KYC';
import Wallet from './pages/Wallet';
import MyListings from './pages/MyListings';
import IndiaMarket from './pages/IndiaMarket';
import GlobalMarket from './pages/GlobalMarket';
import ComingSoon from './pages/ComingSoon';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import HowCCCsWork from './pages/HowCCCsWork';
import ComplianceVsOffset from './pages/ComplianceVsOffset';
import WhoCanParticipate from './pages/WhoCanParticipate';
import News from './pages/News';
import RegistryProjects from './pages/RegistryProjects';
import RegistryProjectDetail from './pages/RegistryProjectDetail';
import IssuanceBatchDetail from './pages/IssuanceBatchDetail';
import Markets from './pages/Markets';
import MarketDetail from './pages/MarketDetail';
import OrderEntry from './pages/OrderEntry';
import CustodyWallet from './pages/CustodyWallet';
import RetireCredits from './pages/RetireCredits';
import RetirementCertificate from './pages/RetirementCertificate';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminKYC from './pages/admin/AdminKYC';
import AdminTransactions from './pages/admin/AdminTransactions';
import AdminListings from './pages/admin/AdminListings';
import AdminMarket from './pages/admin/AdminMarket';
import AdminNews from './pages/admin/AdminNews';
import AdminLeads from './pages/admin/AdminLeads';
import AdminRegistry from './pages/admin/AdminRegistry';
import AdminExchange from './pages/admin/AdminExchange';
import NotFound from './pages/NotFound';

// DaaS pages
import Emissions from './pages/Emissions';
import EmissionsLog from './pages/EmissionsLog';
import SupplyChain from './pages/SupplyChain';
import GreenLogistics from './pages/services/GreenLogistics';
import GreenPackaging from './pages/services/GreenPackaging';
import WasteManagement from './pages/services/WasteManagement';
import GreenProcurement from './pages/services/GreenProcurement';
import Roadmap from './pages/Roadmap';
import Vendors from './pages/Vendors';
import Reports from './pages/Reports';

import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner"/></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner"/></div>;
  if (user) return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: 'var(--surface)',
                color: 'var(--text)',
                border: '1px solid var(--border2)',
                fontFamily: 'Manrope, sans-serif',
                fontSize: '0.88rem',
              },
              success: { iconTheme: { primary: '#00ff88', secondary: '#000' } },
              error: { iconTheme: { primary: '#ff0099', secondary: '#fff' } },
            }}
          />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/market/india" element={<Layout><IndiaMarket /></Layout>} />
            <Route path="/market/global" element={<Layout><GlobalMarket /></Layout>} />
            <Route path="/listings" element={<Layout><Marketplace /></Layout>} />
            <Route path="/listings/:id" element={<Layout><ListingDetail /></Layout>} />
            <Route path="/coming-soon" element={<Layout><ComingSoon /></Layout>} />
            <Route path="/terms" element={<Layout><Terms /></Layout>} />
            <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
            <Route path="/learn/how-cccs-work" element={<Layout><HowCCCsWork /></Layout>} />
            <Route path="/learn/compliance-vs-offset" element={<Layout><ComplianceVsOffset /></Layout>} />
            <Route path="/learn/who-can-participate" element={<Layout><WhoCanParticipate /></Layout>} />
            <Route path="/news" element={<Layout><News /></Layout>} />
            <Route path="/registry/projects" element={<Layout><RegistryProjects /></Layout>} />
            <Route path="/registry/projects/:id" element={<Layout><RegistryProjectDetail /></Layout>} />
            <Route path="/registry/issuance-batches/:id" element={<Layout><IssuanceBatchDetail /></Layout>} />
            <Route path="/markets" element={<Layout><Markets /></Layout>} />
            <Route path="/markets/:id" element={<Layout><MarketDetail /></Layout>} />
            <Route path="/order-entry" element={<Layout><OrderEntry /></Layout>} />
            <Route path="/retire" element={<ProtectedRoute><Layout><RetireCredits /></Layout></ProtectedRoute>} />
            <Route path="/retirements/:id" element={<ProtectedRoute><Layout><RetirementCertificate /></Layout></ProtectedRoute>} />

            {/* Auth */}
            <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
            <Route path="/signup" element={<GuestRoute><Signup /></GuestRoute>} />

            {/* Protected User */}
            <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
            <Route path="/trade" element={<ProtectedRoute><Layout><Trade /></Layout></ProtectedRoute>} />
            <Route path="/portfolio" element={<ProtectedRoute><Layout><Portfolio /></Layout></ProtectedRoute>} />
            <Route path="/transactions" element={<ProtectedRoute><Layout><Transactions /></Layout></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
            <Route path="/kyc" element={<ProtectedRoute><Layout><KYC /></Layout></ProtectedRoute>} />
            <Route path="/wallet" element={<ProtectedRoute><Layout><Wallet /></Layout></ProtectedRoute>} />
            <Route path="/custody-wallet" element={<ProtectedRoute><Layout><CustodyWallet /></Layout></ProtectedRoute>} />
            <Route path="/my-listings" element={<ProtectedRoute><Layout><MyListings /></Layout></ProtectedRoute>} />

            {/* DaaS — Measure */}
            <Route path="/emissions" element={<ProtectedRoute><Layout><Emissions /></Layout></ProtectedRoute>} />
            <Route path="/emissions/log" element={<ProtectedRoute><Layout><EmissionsLog /></Layout></ProtectedRoute>} />
            <Route path="/supply-chain" element={<ProtectedRoute><Layout><SupplyChain /></Layout></ProtectedRoute>} />

            {/* DaaS — Reduce (Green Services) */}
            <Route path="/services/logistics" element={<ProtectedRoute><Layout><GreenLogistics /></Layout></ProtectedRoute>} />
            <Route path="/services/packaging" element={<ProtectedRoute><Layout><GreenPackaging /></Layout></ProtectedRoute>} />
            <Route path="/services/waste" element={<ProtectedRoute><Layout><WasteManagement /></Layout></ProtectedRoute>} />
            <Route path="/services/procurement" element={<ProtectedRoute><Layout><GreenProcurement /></Layout></ProtectedRoute>} />

            {/* DaaS — Plan + Report */}
            <Route path="/roadmap" element={<ProtectedRoute><Layout><Roadmap /></Layout></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Layout><Reports /></Layout></ProtectedRoute>} />

            {/* DaaS — Vendors (public) */}
            <Route path="/vendors" element={<Layout><Vendors /></Layout>} />

            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminLayout><AdminUsers /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/kyc" element={<ProtectedRoute adminOnly><AdminLayout><AdminKYC /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/transactions" element={<ProtectedRoute adminOnly><AdminLayout><AdminTransactions /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/listings" element={<ProtectedRoute adminOnly><AdminLayout><AdminListings /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/market" element={<ProtectedRoute adminOnly><AdminLayout><AdminMarket /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/news" element={<ProtectedRoute adminOnly><AdminLayout><AdminNews /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/leads" element={<ProtectedRoute adminOnly><AdminLayout><AdminLeads /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/registry" element={<ProtectedRoute adminOnly><AdminLayout><AdminRegistry /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/exchange" element={<ProtectedRoute adminOnly><AdminLayout><AdminExchange /></AdminLayout></ProtectedRoute>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
